/**
 * @module nhcmap/interactions/AutoZoomInteraction
 */
import { Interaction } from 'ol/interaction';
import { getCenter, boundingExtent, scaleFromCenter, isEmpty } from 'ol/extent';
import { unByKey } from 'ol/Observable';
import { fromLonLat, toLonLat, transformExtent } from 'ol/proj';
import VectorSource from 'ol/source/Vector';

/**
 * @typedef {Object} Options
 * @property {string[]} [skipLayers] - An array of layer names to ignore during the auto-zoom calculation.
 * @property {number} [latOffset=0] - A latitude offset to apply to the final map center.
 * @property {number} [lonOffset=0] - A longitude offset to apply to the final map center.
 * @property {number} [duration=700] - The duration of the zoom animation in milliseconds.
 */

/**
 * An interaction that automatically zooms the map to the combined extent of all
 * vector and tile layers once they have finished loading.
 *
 * This interaction is "once-off"; it triggers the zoom and then deactivates its listeners.
 */
export default class AutoZoomInteraction extends Interaction {
  /**
   * @param {Options} [options={}] - Options for the interaction.
   */
  constructor(options = {}) {
    super();

    /**
     * @private
     * @type {string[]}
     */
    this.skipLayers_ = options.skipLayers || [];

    /**
     * @private
     * @type {number}
     */
    this.latOffset_ = options.latOffset || 0;

    /**
     * @private
     * @type {number}
     */
    this.duration_ = options.duration || 700;

    /**
     * @private
     * @type {import("ol/Observable").EventsKey[]}
     */
    this.listenerKeys_ = [];

    /**
     * @private
     * @type {Map<import("ol/layer/Layer").default, {featuresReady: boolean, sourceReady: boolean}>}
     */
    this.layerLoadingStatus_ = new Map();

    /**
     * @private
     * @type {boolean}
     */
    this.hasTriggeredZoom_ = false;
  }

  /**
   * @param {import("ol/Map").default | null} map
   * @override
   */
  setMap(map) {
    // This is the core lifecycle method called by OpenLayers.
    // First, call the parent method.
    super.setMap(map);

    if (map) {
      // If a map is provided, it means the interaction is being added.
      // We start the process of listening for layers to load.
      this.startListening_();
    } else {
      // If map is null, it means the interaction is being removed.
      // We clean up any active listeners to prevent memory leaks.
      this.cleanupListeners_();
    }
  }

  /**
   * Finds relevant layers and sets up listeners to wait for them to load.
   * @private
   */
  startListening_() {
    const map = this.getMap();
    if (!map) return;

    const relevantLayers = map.getLayers().getArray().filter(layer =>
      !this.skipLayers_.includes(layer.get('name')) &&
      layer.getVisible() &&
      layer.getSource()
    );

    if (relevantLayers.length === 0) {
      console.warn("AutoZoom: No relevant layers found to auto-zoom to.");
      return;
    }

    // Initialize status for all relevant layers
    relevantLayers.forEach(layer => {
      this.layerLoadingStatus_.set(layer, { 
        featuresReady: false, 
        sourceReady: false 
      });
    });

    // --- Set up event listeners for each relevant layer ---
    relevantLayers.forEach(layer => {
      const source = layer.getSource();
      const layerName = layer.get('name') || 'Unnamed Layer';

      if (source instanceof VectorSource) {
        // For Vector sources, wait for features to load.
        const featuresLoadEndKey = source.on('featuresloadend', () => {
          const status = this.layerLoadingStatus_.get(layer);
          if (status) {
            status.featuresReady = true;
            status.sourceReady = true; // Implies source is also ready
          }
          if (this.areAllLayersReady_()) {
            this.triggerAutoZoom_(relevantLayers);
          }
        });
        this.listenerKeys_.push(featuresLoadEndKey);

        const featuresLoadErrorKey = source.on('featuresloaderror', () => {
          console.error(`AutoZoom: Error loading features for vector layer '${layerName}'. Treating as ready.`);
          const status = this.layerLoadingStatus_.get(layer);
          if (status) {
            status.featuresReady = true;
            status.sourceReady = true;
          }
          if (this.areAllLayersReady_()) {
            this.triggerAutoZoom_(relevantLayers);
          }
        });
        this.listenerKeys_.push(featuresLoadErrorKey);

        // Check initial state
        if (source.getState() === 'ready' && source.getFeatures().length > 0) {
          const status = this.layerLoadingStatus_.get(layer);
          if (status) {
            status.featuresReady = true;
            status.sourceReady = true;
          }
        }
      }
      else {
        // For other sources (like Tile, Image), wait for the source state to be 'ready'.
        const changeKey = source.on('change', () => {
          if (source.getState() === 'ready') {
            const status = this.layerLoadingStatus_.get(layer);
            if (status) {
              status.sourceReady = true;
            }
            if (this.areAllLayersReady_()) {
              this.triggerAutoZoom_(relevantLayers);
            }
          }
        });
        this.listenerKeys_.push(changeKey);

        // Check initial state
        if (source.getState() === 'ready') {
          const status = this.layerLoadingStatus_.get(layer);
          if (status) {
            status.sourceReady = true;
          }
        }
      }
    });

    // A small timeout allows the main thread to clear and ensures all initial
    // 'ready' states are correctly captured before the first check.
    setTimeout(() => {
        if (this.areAllLayersReady_()) {
            this.triggerAutoZoom_(relevantLayers);
        }
    }, 0);
  }

  /**
   * Checks if all relevant layers are in a "ready" state.
   * @returns {boolean}
   * @private
   */
  areAllLayersReady_() {
    for (const [layer, status] of this.layerLoadingStatus_.entries()) {
      const source = layer.getSource();
      if (source instanceof VectorSource) {
        if (!status.featuresReady || source.getState() !== 'ready') return false;
      }
      else {
        if (!status.sourceReady || source.getState() !== 'ready') return false;
      }
    }
    return true;
  }

  /**
   * Triggers the zoom calculation and animation, then cleans up.
   * @param {Array<import("ol/layer/Layer").default>} layers
   * @private
   */
  triggerAutoZoom_(layers) {
    if (this.hasTriggeredZoom_) return;
    this.hasTriggeredZoom_ = true;

    this.zoomToCombinedExtent_(layers);
    this.cleanupListeners_();
  }

  /**
   * Removes all event listeners.
   * @private
   */
  cleanupListeners_() {
    this.listenerKeys_.forEach(key => unByKey(key));
    this.listenerKeys_ = [];
  }

  /**
   * Zooms the map to the combined extent of the given layers.
   * @param {Array<import("ol/layer/Layer").default>} layers - The layers to include in the zoom.
   * @private
   */
  zoomToCombinedExtent_(layers) {
    const map = this.getMap();
    if (!map) return;
    const view = map.getView();
    const projection = view.getProjection();

    let combinedExtent = boundingExtent([]);

    // First, try to get extents from layer sources
    layers.forEach(layer => {
      const source = layer.getSource();
      if (source && source.getExtent) {
        const layerExtent = source.getExtent();
        if (layerExtent && !isEmpty(layerExtent)) {
          combinedExtent = boundingExtent([...combinedExtent, ...layerExtent]);
        }
      }
    });
    
    // Fallback to feature-based extent if source extent is not reliable
    if (isEmpty(combinedExtent)) {
      let mercatorCoords = [];
      layers.forEach(layer => {
        const source = layer.getSource();
        if (source instanceof VectorSource) {
          source.getFeatures().forEach(feature => {
            const geom = feature.getGeometry();
            if (geom) {
              const featureExtent = geom.getExtent();
              mercatorCoords.push([featureExtent[0], featureExtent[1]]);
              mercatorCoords.push([featureExtent[2], featureExtent[3]]);
            }
          });
        }
      });
      if (mercatorCoords.length > 0) {
          combinedExtent = boundingExtent(mercatorCoords);
      }
    }

    if (isEmpty(combinedExtent)) {
      console.warn("AutoZoom: No coordinates found. Skipping auto-zoom.");
      return;
    }
    
    const lonLatExtent = transformExtent(combinedExtent, projection, 'EPSG:4326');
    const height = Math.abs(lonLatExtent[3] - lonLatExtent[1]);

    let minLon = lonLatExtent[0];
    let maxLon = lonLatExtent[2];
    
    // If the extent is wider than 180°, it likely crosses the dateline
    // Simplified dateline logic. If this isn't working well, look at
    // repo for MapManager.js for a more complex way of gathering
    // allLons based on allCoords.
    if (maxLon - minLon > 180) {
      const allLons = [];
      layers.forEach(layer => {
        const source = layer.getSource();
        if(source instanceof VectorSource) {
          source.getFeatures().forEach(feature => {
            const coords = toLonLat(getCenter(feature.getGeometry().getExtent()), projection);
            allLons.push(coords[0] < 0 ? coords[0] + 360 : coords[0]);
          });
        }
      });
      minLon = Math.min(...allLons);
      maxLon = Math.max(...allLons);

      const adjustedLonLatExtent = [minLon, lonLatExtent[1], maxLon, lonLatExtent[3]];
      combinedExtent = transformExtent(adjustedLonLatExtent, 'EPSG:4326', projection);
    }

    // Calculate scale factor based on height. For heights ≤ 10°, use scale 
    // factor 2.0. For heights ≥ 20°, use scale factor 1.25. For heights between
    // 10° and 20°, interpolate linearly between 2.0 and 1.25. These numbers
    // are educated guesses at this point and may require adjustment.
    let scaleFactor;
    if (height <= 10) scaleFactor = 2.0;
    else if (height >= 20) scaleFactor = 1.25;
    // Linear interpolation between 2.0 and 1.25
    else scaleFactor = 2.0 - ((height - 10) / 10) * (2.0 - 1.25);

    scaleFromCenter(combinedExtent, scaleFactor);
    const resolution = view.getResolutionForExtent(combinedExtent);
    const zoom = view.getZoomForResolution(resolution);
    
    // Get the center in lon/lat, apply latitude offset, then transform back
    let center = getCenter(combinedExtent);
    let centerLonLat = toLonLat(center, projection);
    centerLonLat[1] += this.latOffset_;

    // Adjust the center to ensure it crosses the dateline correctly
    let currentCenter = toLonLat(view.getCenter(), 'EPSG:3857');
    let targetCenter = centerLonLat.slice(); // already in lon/lat
    // Calculate longitude difference
    let lonDiff = targetCenter[0] - currentCenter[0];
    if (Math.abs(lonDiff) > 180) {
      // Adjust target longitude for shortest path
      if (lonDiff > 0) {
        targetCenter[0] -= 360;
      } else {
        targetCenter[0] += 360;
      }
    }
    let newCenter = fromLonLat(targetCenter, 'EPSG:3857');

    view.animate({
      duration: 700,
      center: newCenter,
      zoom: zoom
    });
  }
}
